import { useEffect, useMemo, useState } from 'react';
import { getProjectPhasesGrpc } from '../../../shared/api/grpc/revitClient';
import { isGrpcRuntimeAvailable } from '../../../shared/api/grpc/transport';
import { useActiveDocumentInfoContext } from '../../../shared/hooks/ActiveDocumentProvider';
import type { DropdownOption } from '../../../shared/ui/forms/SearchableDropdown';

interface ProjectPhaseState {
  phaseNames: string[];
  isLoading: boolean;
  error: string | null;
}

const EMPTY_STATE: ProjectPhaseState = {
  phaseNames: [],
  isLoading: false,
  error: null,
};

const phaseStateCache = new Map<string, ProjectPhaseState>();
const phaseRequests = new Map<string, Promise<ProjectPhaseState>>();

function normalizePhaseNames(phaseNames: string[]): string[] {
  const seen = new Set<string>();
  return phaseNames.reduce<string[]>((names, phaseName) => {
    const normalized = phaseName.trim();
    const key = normalized.toLocaleLowerCase();
    if (!normalized || seen.has(key)) {
      return names;
    }

    seen.add(key);
    names.push(normalized);
    return names;
  }, []);
}

function loadProjectPhases(scopeKey: string): Promise<ProjectPhaseState> {
  const existingRequest = phaseRequests.get(scopeKey);
  if (existingRequest) {
    return existingRequest;
  }

  const request = getProjectPhasesGrpc()
    .then((result) => {
      const state: ProjectPhaseState = result.success
        ? {
            phaseNames: normalizePhaseNames(result.phaseNames),
            isLoading: false,
            error: null,
          }
        : {
            ...EMPTY_STATE,
            error: result.error ?? 'Unable to load project phases',
          };
      if (result.success) {
        phaseStateCache.set(scopeKey, state);
      }
      return state;
    })
    .finally(() => {
      phaseRequests.delete(scopeKey);
    });

  phaseRequests.set(scopeKey, request);
  return request;
}

export function useProjectPhaseOptions() {
  const { projectKey, modelVersion } = useActiveDocumentInfoContext();
  const isRuntimeAvailable = isGrpcRuntimeAvailable();
  const scopeKey = projectKey
    ? projectKey + ':' + (modelVersion ?? 'unknown')
    : null;
  const [state, setState] = useState<ProjectPhaseState>(EMPTY_STATE);
  const [loadedScopeKey, setLoadedScopeKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadedScopeKey(scopeKey);

    if (!scopeKey || !isRuntimeAvailable) {
      setState(EMPTY_STATE);
      return () => {
        cancelled = true;
      };
    }

    const cachedState = phaseStateCache.get(scopeKey);
    if (cachedState) {
      setState(cachedState);
      return () => {
        cancelled = true;
      };
    }

    setState({
      phaseNames: [],
      isLoading: true,
      error: null,
    });

    void loadProjectPhases(scopeKey).then((nextState) => {
      if (!cancelled) {
        setState(nextState);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isRuntimeAvailable, scopeKey]);

  const visibleState = loadedScopeKey === scopeKey ? state : EMPTY_STATE;
  const options = useMemo<DropdownOption[]>(
    () =>
      visibleState.phaseNames.map((phaseName) => ({
        value: phaseName,
        label: phaseName,
        group: 'Revit project phases',
      })),
    [visibleState.phaseNames],
  );

  return {
    ...visibleState,
    options,
    scopeKey,
    isRuntimeAvailable,
  };
}

export function __resetProjectPhaseOptionsCacheForTests(): void {
  phaseStateCache.clear();
  phaseRequests.clear();
}
